import fuzzysort from "fuzzysort"
import PropTypes from "prop-types"
import React, { Component } from "react"

import BrandAndModel from "./BrandAndModel"
import styles from "./BrandAndModelSelector.module.sass"

const createBrandAndModelObject = (obj) => {
  return {
    label: obj.model ? obj.model : obj.brand,
    value: obj,
  }
}

class BrandAndModelSelector extends Component {
  static propTypes = {
    autoFocus: PropTypes.bool,
    topBrands: PropTypes.array.isRequired, // ["Audi", "Nissan", "Ford"]
    all: PropTypes.array.isRequired, // [["Audi", ["A3", "A4", "Q5"]], ["BMW", ["112"]]]
    doneCallback: PropTypes.func,
    initiallySelected: PropTypes.object,
    initialText: PropTypes.string,
    labels: PropTypes.object,
    visibleBrands: PropTypes.array.isRequired,
    theme: PropTypes.object.isRequired,
    topModelsForBrand: PropTypes.object,
  }

  state = this.initialState()

  initialState() {
    const state = {
      inputFocused: this.props.autoFocus,
    }

    const initiallySelectedBrandAndModel = this.initiallySelectedBrandAndModel()

    state.typedText = initiallySelectedBrandAndModel.inputText || ""
    state.brand = initiallySelectedBrandAndModel.brand
    state.model = initiallySelectedBrandAndModel.model
    state.results = this.initialResults(
      initiallySelectedBrandAndModel.brand,
      state.typedText
    )

    return state
  }

  // Selects brand and model based on initial props
  initiallySelectedBrandAndModel() {
    const { initiallySelected, initialText } = this.props

    if (initiallySelected && initiallySelected.brand) {
      return {
        brand: initiallySelected.brand,
        model: initiallySelected.car_model,
        inputText: initiallySelected.car_model || "",
      }
    }

    if (!initialText) return {}

    return this.brandAndModelFromText(initialText)
  }

  // Returns a matching brand and model from a text input
  // e.g. "Nissan Qashqai" => { brand: "Nissan", model: "Qashqai", inputText: "Qashqai" }
  brandAndModelFromText(text) {
    const splitText = text.trim().split(" ")
    const first = splitText[0]
    let brandMatch = this.matchBrandText(first)
    let inputText = ""
    if (brandMatch) {
      // No more text to match model
      if (splitText.length === 1) return { brand: brandMatch[0] }

      inputText = splitText.slice(1).join(" ")
    } else {
      // Brand not yet found, try to match first 2 words
      brandMatch = this.matchBrandText(splitText.slice(0, 2).join(" "))
      if (!brandMatch) return { inputText: text }

      inputText = splitText.slice(2).join(" ")
    }

    const model = brandMatch[1].find(
      (m) => m.toLowerCase() === inputText.toLowerCase()
    )

    return { brand: brandMatch[0], model, inputText }
  }

  matchBrandText(text) {
    return this.props.all.find(
      (bm) => bm[0].toLowerCase() === text.toLowerCase()
    )
  }

  initialResults(brand, typedText) {
    if (!brand && !typedText) return this.defaultBrandsResults()

    if (typedText) return this.searchResultsFor(brand, typedText)

    return [
      { options: this.topModelOptionsForBrand(brand) },
      { category: "A-Z", options: this.allModelOptionsForBrand(brand) },
    ]
  }

  defaultBrandsResults() {
    return [
      {
        options: this.props.topBrands.map((brand) =>
          createBrandAndModelObject({ brand })
        ),
      },
      {
        category: "A-Z",
        options: this.props.visibleBrands.map((brand) =>
          createBrandAndModelObject({ brand })
        ),
      },
    ]
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.typedText !== prevState.typedText ||
      this.state.brand !== prevState.brand
    ) {
      this.setState((prevState) => ({
        results: this.searchResultsFor(prevState.brand, prevState.typedText),
      }))
    }
  }

  topModelOptionsForBrand(brand) {
    const topModelsForBrand = this.props.topModelsForBrand[brand]

    if (!topModelsForBrand) return this.allModelOptionsForBrand(brand)

    return topModelsForBrand.map((model) =>
      createBrandAndModelObject({ brand, model })
    )
  }

  allModelOptionsForBrand(brand) {
    return this.props.all
      .find(([b]) => b === brand)[1]
      .map((model) => createBrandAndModelObject({ brand, model }))
  }

  selectItem = (item) => {
    this.setState({ ...item, typedText: "", inputFocused: true })

    if (item.model) {
      this.props.doneCallback({
        brand: item.brand,
        car_model: item.model,
      })
    }
  }

  textInputChanged = (value) => {
    this.setState({ typedText: value })
  }

  textInputBlurred = (e) => {
    this.setState({ inputFocused: false })
  }

  textInputKeyUp = (e) => {
    if (e.keyCode === 8 && this.state.typedText === "" && this.state.brand) {
      // Backspace from model selection
      e.preventDefault() // We don't want to start ereasing the brand name
      this.setState((prevState) => ({
        brand: null,
        model: null,
        typedText: prevState.brand,
      }))
    } else if (
      e.key === " " &&
      !this.state.brand &&
      this.state.typedText.toLowerCase() ===
        this.state.results[0].options[0].value.brand.toLowerCase()
    ) {
      this.setState({
        brand: this.state.results[0].options[0].value.brand,
        typedText: "",
      })
      e.preventDefault() // We don't want to start ereasing the brand name
    }
  }

  // Returns the search results for a given input text, which
  // can be either brands (if a brand is not provided), or models
  searchResultsFor(brand, inputText) {
    if (!brand) {
      if (!inputText) {
        // Default brands list - top + other
        return this.defaultBrandsResults()
      }

      // Searching in all brands
      const results = fuzzysort
        .go(
          inputText,
          this.props.all.map(([brand]) => ({ brand })),
          { key: "brand" }
        )
        .map(({ obj }) => createBrandAndModelObject(obj))

      return results.length > 0
        ? [{ options: results }]
        : this.defaultBrandsResults()
    }

    // Searching in models of the selected brand
    const models = this.props.all
      .find(([b]) => b === brand)[1]
      .map((model) => ({ brand, model }))
    if (inputText) {
      const results = fuzzysort
        .go(inputText, models, { key: "model" })
        .map(({ obj }) => createBrandAndModelObject(obj))
      return [{ options: results }]
    }

    const allModelOptions = models.map((model) =>
      createBrandAndModelObject(model)
    )
    const topModelsForBrand = this.props.topModelsForBrand[brand]
    if (!topModelsForBrand) return [{ options: allModelOptions }]

    return [
      { options: this.topModelOptionsForBrand(brand) },
      { category: "A-Z", options: allModelOptions },
    ]
  }

  getInputPlaceholder() {
    // When there are not different placeholder for make and model
    if (
      !this.props.labels.placeholders.brand ||
      !this.props.labels.placeholders.model
    ) {
      return this.props.labels.placeholders.brand_and_model_name
    }

    if (this.state.brand) {
      return this.props.labels.placeholders.model
    }
    return this.props.labels.placeholders.brand
  }

  render() {
    return (
      <BrandAndModel
        autoFocus={this.props.autoFocus}
        prefix={this.state.brand}
        value={this.state.typedText}
        onChange={this.textInputChanged}
        onBlur={this.textInputBlurred}
        onKeyDown={this.textInputKeyUp}
        focused={this.state.inputFocused}
        classNameInput={styles.input}
        inputPlaceholder={this.getInputPlaceholder()}
        choices={this.state.results}
        selectItem={this.selectItem}
        theme={this.props.theme}
        labelSeeAll={this.props.labels.seeAll}
        labelClose={this.props.labels.close}
      />
    )
  }
}

export default BrandAndModelSelector
